/**
 * Réduction des images avant envoi.
 *
 * Un téléphone récent photographie en quatre mille pixels de large et produit
 * un fichier de plusieurs mégaoctets. La fiche d'un article en montre une
 * vignette et, au plus, un aperçu plein écran : conserver l'original ferait
 * attendre l'envoi sur une connexion de chantier, remplirait le stockage et
 * ralentirait chaque affichage du rayon, pour une finesse que personne ne voit.
 *
 * La réduction se fait donc ici, avant le départ. Le serveur garde sa propre
 * limite : celle-ci épargne le transfert, elle ne le remplace pas.
 */

/** Côté le plus long conservé, en pixels. */
export const COTE_MAX = 1200;

/** Qualité du JPEG produit : au-delà, le poids monte sans gain visible. */
export const QUALITE = 0.82;

/**
 * Types que l'on ne retouche pas.
 *
 * Le PDF n'est pas une image. Le GIF peut être animé, et le faire passer par
 * un canevas n'en garderait que la première vue.
 */
const INTOUCHABLES = ['application/pdf', 'image/gif'];

/**
 * Dimensions d'une image ramenée dans un carré, sans déformation.
 *
 * @param {number} largeur largeur d'origine
 * @param {number} hauteur hauteur d'origine
 * @param {number} [max] côté le plus long admis
 * @returns {{largeur: number, hauteur: number}} dimensions à utiliser
 */
export function dimensionsReduites(largeur, hauteur, max = COTE_MAX) {
  const l = Math.round(largeur) || 0;
  const h = Math.round(hauteur) || 0;
  if (l <= 0 || h <= 0) {
    return { largeur: 0, hauteur: 0 };
  }
  const cote = Math.max(l, h);
  if (cote <= max) {
    return { largeur: l, hauteur: h };
  }
  const facteur = max / cote;
  // Au moins un pixel : une image très allongée ramènerait sinon un côté à
  // zéro, et le canevas refuserait de la dessiner.
  return {
    largeur: Math.max(1, Math.round(l * facteur)),
    hauteur: Math.max(1, Math.round(h * facteur)),
  };
}

/**
 * Dit si un fichier doit partir tel quel.
 *
 * @param {Blob} fichier fichier choisi
 * @returns {boolean} vrai si la réduction ne s'applique pas
 */
export function partTelQuel(fichier) {
  const type = (fichier?.type || '').toLowerCase();
  return !type.startsWith('image/') || INTOUCHABLES.includes(type);
}

/**
 * Réduit une image à `max` pixels sur son côté le plus long.
 *
 * Ne promet rien d'autre que de faire au mieux : si le navigateur ne sait pas
 * lire le fichier, l'original part, et le serveur tranchera. Mieux vaut un
 * envoi lourd qu'un envoi perdu.
 *
 * @param {Blob} fichier image choisie
 * @param {number} [max] côté le plus long admis
 * @returns {Promise<Blob>} l'image réduite, ou l'original
 */
export function reduitLImage(fichier, max = COTE_MAX) {
  if (!fichier || partTelQuel(fichier)) {
    return Promise.resolve(fichier);
  }
  // Sans adresse d'objet, pas de lecture possible : l'original part, et le
  // serveur tranchera. Mieux vaut un envoi lourd qu'un envoi perdu.
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    return Promise.resolve(fichier);
  }
  return new Promise((resout) => {
    const adresse = URL.createObjectURL(fichier);
    const image = new Image();
    let repondu = false;
    const rend = (resultat) => {
      if (repondu) return;
      repondu = true;
      URL.revokeObjectURL(adresse);
      resout(resultat || fichier);
    };
    image.onerror = () => rend(null);
    image.onload = () => {
      try {
        const { largeur, hauteur } = dimensionsReduites(
          image.naturalWidth,
          image.naturalHeight,
          max,
        );
        if (!largeur || (largeur === image.naturalWidth && hauteur === image.naturalHeight)) {
          rend(null);
          return;
        }
        const canevas = document.createElement('canvas');
        canevas.width = largeur;
        canevas.height = hauteur;
        canevas.getContext('2d').drawImage(image, 0, 0, largeur, hauteur);
        canevas.toBlob((reduite) => rend(reduite), 'image/jpeg', QUALITE);
      } catch (e) {
        rend(null);
      }
    };
    image.src = adresse;
  });
}

/**
 * Lit un fichier en adresse de données, pour les pièces qui voyagent en JSON.
 *
 * @param {Blob} fichier fichier à lire
 * @returns {Promise<string>} l'adresse `data:` correspondante
 */
export function enAdresseDeDonnees(fichier) {
  return new Promise((resout, rejette) => {
    const lecteur = new FileReader();
    lecteur.onload = () => resout(String(lecteur.result || ''));
    lecteur.onerror = () => rejette(new Error('Le fichier n\u2019a pas pu être lu.'));
    lecteur.readAsDataURL(fichier);
  });
}
